import React, { useCallback, useContext, useState } from 'react';

import classNames from 'classnames';
import assignmentCreate from 'images/create-from-scratch.svg';
import assignmentCopy from 'images/reuse-existing.svg';
import assignmentTemplate from 'images/use-a-template.svg';
import { isNil } from 'lodash';
import { useAssignmentsForCopy } from 'sdk';

import { SearchField } from 'pages/Teachers/shared';
import { AssignmentDialogModes } from 'pages/Teachers/shared/AssignmentDialog';
import { MuiAutocompleteChangeReasons } from 'utils/constants';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';
import {
  MixpanelAction,
  MixpanelObject
} from 'utils/integrations/mixpanel/constants';

import AssignmentCreateDialogContext from 'components/AssignmentCreateDialogContext';
import Typography from 'components/Typography';
import UserContext from 'components/UserContext';

import styles from './styles.module.scss';

const AssignmentCreateSelectFlow = ({
  setIsFlowSelected,
  setIsCreateTemplateSelected,
  isMathAssignment = false
}) => {
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const { open: openCreateAssignmentDialog } = useContext(
    AssignmentCreateDialogContext
  );

  const { data: assignments } = useAssignmentsForCopy({
    data: { is_math_assignment: isMathAssignment }
  });

  const { user } = useContext(UserContext);

  const handleCreateFromScratch = useCallback(() => {
    trackMixpanelEvent(
      user.id,
      `${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`,
      {
        feature: 'create',
        view: 'type',
        button: 'create'
      }
    );
    openCreateAssignmentDialog({
      mode: AssignmentDialogModes.CREATE,
      isMathAssignment: isMathAssignment
    });
  }, [openCreateAssignmentDialog, user, isMathAssignment]);

  const handleUseTemplate = useCallback(() => {
    trackMixpanelEvent(
      user.id,
      `${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`,
      {
        feature: 'create',
        view: 'type',
        button: 'template'
      }
    );
    setIsFlowSelected(true);
    setIsCreateTemplateSelected(true);
  }, [setIsFlowSelected, setIsCreateTemplateSelected, user]);

  const handleAssignmentChange = useCallback(
    (event, value, reason) => {
      trackMixpanelEvent(
        user.id,
        `${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`,
        {
          feature: 'create',
          view: 'type',
          button: 'copy'
        }
      );
      if (isNil(value)) return;

      setSelectedAssignment(value);

      if (reason === MuiAutocompleteChangeReasons.SELECT_OPTION) {
        openCreateAssignmentDialog({
          mode: AssignmentDialogModes.COPY,
          selectedAssignment: value,
          isMathAssignment: isMathAssignment
        });
      } else {
        openCreateAssignmentDialog({
          mode: AssignmentDialogModes.CREATE,
          selectedAssignment: value,
          isMathAssignment: isMathAssignment
        });
      }
    },
    [openCreateAssignmentDialog, setSelectedAssignment, user, isMathAssignment]
  );

  const assignmentsForReuse = assignments;

  return (
    <section className={styles.container}>
      <main
        className={classNames(styles.cardsGrid, {
          [styles.withoutTemplates]: !isMathAssignment
        })}
      >
        <div
          className={classNames(styles.card, {
            [styles.smallerCard]: !isMathAssignment
          })}
        >
          <Typography variant="h6" className={styles.cardTitle}>
            CREATE FROM SCRATCH
          </Typography>
          <div className={styles.iconWrapper}>
            <img
              className={styles.icon}
              src={assignmentCreate}
              alt="Assignment Create"
            />
          </div>
          <Typography variant="body2" className={styles.cardDescription}>
            Use our assignment editor to create an assignment.
          </Typography>
          <button
            onClick={handleCreateFromScratch}
            className={styles.buttonSecondary}
            data-intercom-target="Create Start Scratch Button"
          >
            GET STARTED →
          </button>
        </div>

        {isMathAssignment && (
          <div className={styles.card}>
            <Typography variant="H-TEXT-2" className={styles.cardTitle}>
              USE A TEMPLATE
            </Typography>
            <div className={styles.iconWrapper}>
              <img
                className={styles.icon}
                src={assignmentTemplate}
                alt="Assignment Template"
              />
            </div>
            <Typography variant="body2" className={styles.cardDescription}>
              Using a set curriculum? Leverage AI and save time with a template!
            </Typography>
            <button
              onClick={handleUseTemplate}
              className={styles.buttonPrimary}
              data-intercom-target="Create Start Template Button"
            >
              GET STARTED →
            </button>
          </div>
        )}

        <div
          className={classNames(styles.card, {
            [styles.smallerCard]: !isMathAssignment
          })}
        >
          <Typography variant="h6" className={styles.cardTitle}>
            REUSE EXISTING
          </Typography>
          <div className={styles.iconWrapper}>
            <img
              className={styles.icon}
              src={assignmentCopy}
              alt="Assignment Copy"
            />
          </div>
          <Typography variant="body2" className={styles.cardDescription}>
            Modify a copy of an existing assignment.
          </Typography>
          <div className={styles.searchWrapper}>
            <SearchField
              value={selectedAssignment}
              options={assignmentsForReuse || []}
              getOptionLabel={(assignment) => assignment.name}
              onChange={handleAssignmentChange}
              nooptionstext={
                <Typography variant="body2">No Assignments</Typography>
              }
              placeholder="Search your assignments"
              data-intercom-target="Create Start Reuse Button"
            />
          </div>
        </div>
      </main>
    </section>
  );
};

export default AssignmentCreateSelectFlow;
